import { useEffect, useState } from "react";
import {
  AUTOPILOT_LIMITS,
  STRATEGY,
  nativeUnits,
  type AutopilotLimitsRequest,
  type AutopilotStatus,
} from "@auto-pools/core";
import { api, ApiError } from "../api/client";
import { formatAgo, formatMoney, formatPercent2 } from "../lib/format";
import { drawdownLimitValid, entriesLimitValid } from "./autopilotLimitsValidation";
import { oldestExitNowAt, unvaluedFunded } from "./autopilotCycles";
import { useAutopilot } from "./autopilotContext";
import { useViewer } from "../auth/authContext";

const MIN_CAPITAL_USD = STRATEGY.sizing.minEntryUsd;
const CAPITAL_DECIMAL = /^\d+(\.\d+)?$/;

/**
 * The runner revalues a held position on a ~60 s cadence (`VALUE_EVERY_MS`,
 * apps/server/src/autopilot/cycle.ts). Twice that with no newer valuation means the loop stalled or
 * every attempt since has failed — `drawdownPercent` still carries the last equity, so it must stop
 * reading as a live risk number rather than silently understate the current drawdown.
 */
const STALE_VALUATION_MS = 2 * 60_000;
/**
 * A status poll only re-renders this card when it settles (`useResource.ts`); while one is pending
 * past the stale bound, nothing else forces a redraw. This ticks `now` on its own so the marker
 * still appears from render alone, without waiting on network activity.
 */
const STALE_CHECK_INTERVAL_MS = 15_000;

/** Age of the cycle's last exit-now valuation, or null when there is none to age. */
function valuationAge(at: string | null | undefined, now: number): number | null {
  if (!at) return null;
  const time = Date.parse(at);
  return Number.isFinite(time) ? now - time : null;
}

/**
 * The capital and the two operator limits against their current values (design 47, section 5).
 * Editable only while paused — everything else is a plain read-out.
 */
export function AutopilotLimitsCard({ status }: { status: AutopilotStatus }) {
  const autopilot = useAutopilot();
  const view = autopilot.data ?? null;
  const limits = view?.limits ?? null;
  // Design 48: the capital mode changes where the limits do, but only while paused and flat
  // (`AutopilotView.capitalChange`) — a cycle in flight must not race a capital swap under it.
  const capitalChange = view?.capitalChange ?? { allowed: false, reason: null };
  const availableUsd = view?.funding.available ?? null;
  const tokenDecimals = view?.funding.token.decimals ?? 6;
  const viewer = useViewer();
  // Editable only by the operator; a viewer reads the same values.
  const editable = status.state === "paused" && !viewer;
  const [editing, setEditing] = useState(false);
  const [drawdown, setDrawdown] = useState(
    limits?.drawdownPercent ?? AUTOPILOT_LIMITS.drawdownPercent.default,
  );
  const [entries, setEntries] = useState(
    String(limits?.entriesPer24h ?? AUTOPILOT_LIMITS.entriesPer24h.default),
  );
  const [modeWhole, setModeWhole] = useState(status.capitalMode === "whole");
  const [capitalAmount, setCapitalAmount] = useState(status.capital ?? MIN_CAPITAL_USD);
  // Set only by the operator's own capital input; never derived from comparing the draft with a
  // refreshed status, whose capital can move under an open editor when a paused position settles.
  const [capitalDirty, setCapitalDirty] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!editable) setEditing(false);
  }, [editable]);

  const drawdownValid = drawdownLimitValid(drawdown);
  const entriesValid = entriesLimitValid(entries);
  const capitalAmountValid =
    CAPITAL_DECIMAL.test(capitalAmount) &&
    Number(capitalAmount) >= Number(MIN_CAPITAL_USD) &&
    (availableUsd === null || Number(capitalAmount) <= Number(availableUsd));
  // Capital is sent, and its amount bounded, only when the operator touched the mode or the
  // amount: a limits-only edit must not fail because an untouched fixed capital no longer fits
  // the free balance.
  const capitalEdited = capitalChange.allowed && capitalDirty;
  const capitalBlocksSave = capitalEdited && !modeWhole && !capitalAmountValid;

  // Only a held position's valuation can go stale (a flat run's equity is realized capital, always
  // current); no cycle or no valuation yet keeps today's plain "—"/number behaviour unchanged.
  const exitNowAt = oldestExitNowAt(status.cycles);
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    if (exitNowAt === null) return;
    const timer = window.setInterval(() => {
      setNow(Date.now());
    }, STALE_CHECK_INTERVAL_MS);
    return () => {
      window.clearInterval(timer);
    };
  }, [exitNowAt]);
  const age = valuationAge(exitNowAt, now);
  const staleDrawdown = age !== null && age >= STALE_VALUATION_MS;

  async function save() {
    if (!drawdownValid || !entriesValid || pending) return;
    if (capitalBlocksSave) return;
    setPending(true);
    setError(null);
    try {
      const body: AutopilotLimitsRequest = {
        drawdownPercent: drawdown,
        entriesPer24h: Number(entries),
      };
      if (capitalEdited) {
        body.capital = modeWhole
          ? { mode: "whole" }
          : { mode: "fixed", capitalNative: nativeUnits(capitalAmount, tokenDecimals).toString() };
      }
      await api("/autopilot/limits", {
        method: "POST",
        body,
        sensitive: true,
      });
      setEditing(false);
      setCapitalDirty(false);
      autopilot.reload();
    } catch (reason) {
      setError(
        reason instanceof ApiError
          ? reason.message
          : reason instanceof Error
            ? reason.message
            : "Limits were not saved",
      );
    } finally {
      setPending(false);
    }
  }

  return (
    <section className="panel autopilot-limits-card" aria-label="Autopilot limits">
      <header>
        <strong>Limits</strong>
        {editable && !editing && (
          <button
            type="button"
            className="button ghost small"
            onClick={() => {
              // Resync the drafts to the current server limits when editing starts (D3): the
              // mount-time defaults above can be stale by the time the operator opens the editor.
              setDrawdown(limits?.drawdownPercent ?? AUTOPILOT_LIMITS.drawdownPercent.default);
              setEntries(String(limits?.entriesPer24h ?? AUTOPILOT_LIMITS.entriesPer24h.default));
              setModeWhole(status.capitalMode === "whole");
              setCapitalAmount(status.capital ?? MIN_CAPITAL_USD);
              setCapitalDirty(false);
              setEditing(true);
            }}
          >
            Edit
          </button>
        )}
      </header>
      <dl className="autopilot-limits-list">
        <div>
          <dt>Capital</dt>
          {editing ? (
            <div className="autopilot-limits-capital-edit">
              <label className="autopilot-whole-checkbox">
                <input
                  type="checkbox"
                  checked={modeWhole}
                  disabled={!capitalChange.allowed}
                  onChange={(e) => {
                    setModeWhole(e.target.checked);
                    setCapitalDirty(true);
                  }}
                />
                <span>Use the whole free balance</span>
              </label>
              {!modeWhole && (
                <input
                  inputMode="decimal"
                  aria-label="Capital (USDC)"
                  value={capitalAmount}
                  disabled={!capitalChange.allowed}
                  aria-invalid={capitalEdited && !capitalAmountValid}
                  onChange={(e) => {
                    setCapitalAmount(e.target.value);
                    setCapitalDirty(true);
                  }}
                />
              )}
              {!capitalChange.allowed && (
                <small className="autopilot-limits-capital-reason">
                  {capitalChange.reason ?? "Capital changes only while paused and flat."}
                </small>
              )}
            </div>
          ) : (
            <>
              <dd className="num">{status.capital !== null ? formatMoney(status.capital) : "—"}</dd>
              <small>
                {status.capitalMode === "whole"
                  ? "whole free balance · read at every entry"
                  : "profit reinvested"}
              </small>
              {status.regime.half && (
                <small>
                  {status.regime.weekReturnPercent !== null
                    ? `Входы уменьшены вдвое: неделя ${formatPercent2(status.regime.weekReturnPercent)}`
                    : "Входы уменьшены вдвое"}
                </small>
              )}
            </>
          )}
        </div>
        <div>
          <dt>Drawdown from peak</dt>
          {editing ? (
            <input
              inputMode="decimal"
              value={drawdown}
              aria-invalid={!drawdownValid}
              onChange={(e) => {
                setDrawdown(e.target.value);
              }}
            />
          ) : (
            <>
              <dd className="num">
                {status.drawdownPercent !== null ? formatPercent2(status.drawdownPercent) : "—"} of{" "}
                {limits?.drawdownPercent ?? "—"}%
              </dd>
              {status.peak !== null && (
                <small>transfers excluded · peak {formatMoney(status.peak)}</small>
              )}
              {unvaluedFunded(status.cycles) ? (
                <small>stale · not valued</small>
              ) : (
                staleDrawdown && <small>stale · valued {formatAgo(exitNowAt)}</small>
              )}
            </>
          )}
        </div>
        <div>
          <dt>Entries per 24 h</dt>
          {editing ? (
            <input
              inputMode="numeric"
              value={entries}
              aria-invalid={!entriesValid}
              onChange={(e) => {
                setEntries(e.target.value);
              }}
            />
          ) : (
            <dd className="num">
              {status.entries24h ?? "—"} of {limits?.entriesPer24h ?? "—"}
            </dd>
          )}
        </div>
      </dl>
      {error && (
        <p className="inline-alert warning" role="alert">
          {error}
        </p>
      )}
      {editing && (
        <footer className="autopilot-limits-actions">
          <button
            type="button"
            className="button ghost"
            onClick={() => {
              setEditing(false);
              setDrawdown(limits?.drawdownPercent ?? AUTOPILOT_LIMITS.drawdownPercent.default);
              setEntries(String(limits?.entriesPer24h ?? AUTOPILOT_LIMITS.entriesPer24h.default));
              setModeWhole(status.capitalMode === "whole");
              setCapitalAmount(status.capital ?? MIN_CAPITAL_USD);
              setCapitalDirty(false);
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="button primary"
            disabled={!drawdownValid || !entriesValid || pending || capitalBlocksSave}
            onClick={() => void save()}
          >
            {pending ? "Saving…" : "Save limits"}
          </button>
        </footer>
      )}
    </section>
  );
}
